import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'active' || s === 'trialing') return 'success';
  if (s === 'past_due') return 'warning';
  return 'default';
}

export default function PlatformTenantSubscriptionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-subscriptions/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setSub(json.subscription);
    } catch (e) {
      setError(e.message);
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const removeDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-subscriptions/${id}`, { method: 'DELETE', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      setConfirmDelete(false);
      navigate('/platform/tenant-subscriptions');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !sub) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!sub) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">Subscription #{sub.id}</Typography>
          <Typography variant="body2" color="text.secondary">
            {sub.tenant_name} · {sub.plan_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/tenant-subscriptions')}>
            Back
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/platform/tenants/${sub.tenant_id}`)}>
            Tenant
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/platform/plans/${sub.plan_id}`)}>
            Plan
          </Button>
          {isSuper && (
            <Button variant="contained" onClick={() => navigate(`/platform/tenant-subscriptions/${id}/edit`)}>
              Edit
            </Button>
          )}
          {isSuper && sub.subscription_status === 'draft' && (
            <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
              Delete draft
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip size="small" label={sub.subscription_status} color={statusColor(sub.subscription_status)} />
            </Box>
          </Grid>
          {row('Billing cycle', sub.billing_cycle)}
          {row('Price snapshot', sub.price_snapshot != null ? sub.price_snapshot.toFixed(2) : '—')}
          {row('Start date', sub.start_date)}
          {row('End date', sub.end_date)}
          {row('Renewal date', sub.renewal_date)}
          {row('Cancellation date', sub.cancellation_date)}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Notes
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {sub.notes || '—'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Allowed transitions are enforced server-side (e.g. active → past_due | canceled | expired). New live
          subscriptions auto-cancel other live rows for the tenant.
        </Typography>
      </Paper>

      <Dialog open={confirmDelete} onClose={() => !busy && setConfirmDelete(false)}>
        <DialogTitle>Delete draft subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={removeDraft} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
